/* ═══════════════════════════════════════════════════════════════════════
   A business's own place, with crAIg as the point of it.

   ── The conversation is the hero, and everything else is furniture ─────
   The first version of this put the reading beside the chat, and it was
   wrong in the way business homepages are always wrong: it invited a
   business to write. Give someone a page with sections on it and they
   will fill the sections, and their customer arrives at a wall of prose
   with the useful thing tucked down the right-hand side.

   So the layout is not negotiable and there is no "where should the chat
   go" setting. The conversation is wide, first, and the largest thing on
   the screen. What the business writes sits UNDER it, in short compact
   blocks that answer questions rather than making a case — because the
   pitch is not the page, the pitch is that a person can describe their
   problem in their own words and get somewhere.

   ── Which is why the long ones fold ────────────────────────────────────
   An "about" of six paragraphs shows its first and offers the rest.
   Questions are collapsed to their questions. Nothing here can push the
   conversation off a phone screen, whatever anybody types.

   ── Everything is text ─────────────────────────────────────────────────
   Written by one business, rendered to another's customers. No markup, no
   HTML, no remote images. React escapes every string below; the shape in
   core/overlay.ts is what stops anything else arriving.
   ═══════════════════════════════════════════════════════════════════════ */

import { useState, type ReactNode } from "react";
import type { PageConfig, PageBlock, TenantView } from "../api";

export function BusinessPage({
  tenant,
  page,
  chat,
}: {
  tenant: TenantView;
  page: PageConfig;
  /** The live conversation. This component decides where it sits, never what it does. */
  chat: ReactNode;
}) {
  const blocks = page.blocks ?? [];
  const notices = blocks.filter((b): b is Extract<PageBlock, { kind: "notice" }> => b.kind === "notice");
  const facts = blocks.filter((b): b is Extract<PageBlock, { kind: "facts" }> => b.kind === "facts");
  const rest = blocks.filter((b) => b.kind !== "notice" && b.kind !== "facts");

  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 py-6">
      {notices.map((n, i) => (
        <div
          key={i}
          role="status"
          className="card px-4 py-3 mb-4"
          style={{ background: "var(--warn-tint)", borderColor: "var(--warn)", fontSize: 14 }}
        >
          {n.body}
        </div>
      ))}

      {/* Deliberately compact. A name, a line, and out of the way. */}
      <header className="mb-5">
        {tenant.branding.logoUrl && (
          <img
            src={tenant.branding.logoUrl}
            alt={tenant.displayName}
            style={{ maxHeight: 52, maxWidth: 220, width: "auto", marginBottom: 12, display: "block" }}
          />
        )}
        <h1
          style={{
            fontSize: "clamp(21px, 3.2vw, 28px)",
            fontWeight: 680,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
            margin: 0,
          }}
        >
          {page.headline || tenant.displayName}
        </h1>
        {page.intro && (
          <p
            style={{
              fontSize: 15.5,
              color: "var(--ink-2)",
              lineHeight: 1.55,
              marginTop: 8,
              maxWidth: 560,
            }}
          >
            {page.intro}
          </p>
        )}
      </header>

      {/* The point of the page. Nothing above it competes for the eye. */}
      {chat}

      {/* Facts first of the rest — hours and service area are what people
          scroll for, and they fit on one line each. */}
      {facts.map((f, i) => (
        <div key={i} className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
          {f.rows.map((r, j) => (
            <div key={j} style={{ fontSize: 13.5 }}>
              <span style={{ color: "var(--ink-3)" }}>{r.label}: </span>
              <span style={{ fontWeight: 550 }}>{r.value}</span>
            </div>
          ))}
        </div>
      ))}

      {rest.length > 0 && (
        <div className="mt-5 space-y-3">
          {rest.map((b, i) => (
            <Block key={i} block={b} />
          ))}
        </div>
      )}
    </div>
  );
}

function Block({ block }: { block: PageBlock }) {
  switch (block.kind) {
    case "about": {
      const paras = block.body.split(/\n\s*\n/).filter(Boolean);
      return (
        <Panel title={block.title}>
          <Folded paras={paras} />
        </Panel>
      );
    }

    case "services":
      return (
        <Panel title={block.title ?? "What we do"}>
          <div className="flex flex-wrap gap-2">
            {block.items.map((it, i) => (
              <span
                key={i}
                className="px-3 py-1.5"
                style={{
                  border: "1px solid var(--line)",
                  borderRadius: 999,
                  fontSize: 13.5,
                  fontWeight: 550,
                }}
                title={it.body ?? undefined}
              >
                {it.label}
              </span>
            ))}
          </div>
        </Panel>
      );

    case "questions":
      return (
        <Panel title={block.title ?? "Questions people ask"}>
          {/* Collapsed to the questions. Somebody scanning for one answer
              should not have to read four. */}
          <div className="space-y-1">
            {block.items.map((it, i) => (
              <details key={i}>
                <summary style={{ fontSize: 14, fontWeight: 580, cursor: "pointer", padding: "4px 0" }}>
                  {it.q}
                </summary>
                <p style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.55, margin: "2px 0 8px" }}>
                  {it.a}
                </p>
              </details>
            ))}
          </div>
        </Panel>
      );

    default:
      return null;
  }
}

/** First paragraph, and the rest on request. */
function Folded({ paras }: { paras: string[] }) {
  const [open, setOpen] = useState(false);
  const shown = open ? paras : paras.slice(0, 1);
  return (
    <>
      {shown.map((p, i) => (
        <p
          key={i}
          style={{ fontSize: 14.5, lineHeight: 1.6, color: "var(--ink-2)", marginTop: i ? 8 : 0 }}
        >
          {p}
        </p>
      ))}
      {paras.length > 1 && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            marginTop: 6,
            font: "inherit",
            fontSize: 13.5,
            color: "var(--accent)",
            cursor: "pointer",
          }}
        >
          Read more
        </button>
      )}
    </>
  );
}

function Panel({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="card px-4 py-4">
      {title && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 650,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--ink-3)",
            marginBottom: 8,
          }}
        >
          {title}
        </div>
      )}
      {children}
    </section>
  );
}
